import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  ShoppingBag,
  Package,
  Eye,
  Calendar,
  Download,
  Filter,
  Store,
  Target,
  Activity,
  RefreshCw,
  AlertCircle,
  Map,
  DollarSign,
  Percent,
  Clock,
  FileDown,
  Sparkles,
  ArrowUp,
  ArrowDown,
  ChevronDown
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Treemap,
  ComposedChart,
  Scatter
} from 'recharts';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import toast from 'react-hot-toast';
import ApiService from '../services/api';

const Analytics: React.FC = () => {
  const [dateRange, setDateRange] = useState('last_30_days');
  const [selectedMerchant, setSelectedMerchant] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [useGraphQL, setUseGraphQL] = useState(true);
  const [exportFormat, setExportFormat] = useState<'csv' | 'json' | 'pdf'>('csv');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'geographic' | 'ai'>('overview');
  
  // Use the custom analytics hook
  const { data: analyticsData, loading, error, refetch, isUsingGraphQL } = useAnalyticsData(dateRange, useGraphQL);

  // Load additional data for filters
  const [merchants, setMerchants] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);

  useEffect(() => {
    loadFilterData();
  }, []);

  const loadFilterData = async () => {
    try {
      const [merchantsData, citiesData] = await Promise.all([
        ApiService.getMerchants(),
        ApiService.getCities()
      ]);
      setMerchants(merchantsData || []);
      setCities(citiesData || []);
    } catch (error) {
      console.error('Error loading filter data:', error);
    }
  };

  // Export functionality
  const handleExport = async () => {
    try {
      toast.loading('جاري تصدير البيانات...');
      
      const response = await ApiService.exportStats({
        format: exportFormat,
        date_range: dateRange,
        merchant_id: selectedMerchant !== 'all' ? selectedMerchant : undefined,
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        include_charts: true
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: exportFormat === 'csv' ? 'text/csv' : 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      link.click();
      
      toast.dismiss();
      toast.success('تم تصدير البيانات بنجاح');
    } catch (error) {
      toast.dismiss();
      toast.error('فشل تصدير البيانات');
      console.error('Export error:', error);
    }
  };

  // Filter data based on selections
  const getFilteredData = () => {
    if (!analyticsData) return null;
    
    let filteredData = { ...analyticsData };
    
    // Filter by merchant
    if (selectedMerchant !== 'all') {
      filteredData.merchants = filteredData.merchants.filter(
        m => m.name === selectedMerchant
      );
    }
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filteredData.categories = filteredData.categories.filter(
        c => c.name === selectedCategory
      );
    }
    
    return filteredData;
  };
  
  const filteredData = getFilteredData();

  // Calculate growth rates and changes
  const calculateGrowth = (current: number, previous: number) => {
    if (previous === 0) return { value: 100, type: 'positive' };
    const growth = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(growth),
      type: growth >= 0 ? 'positive' : 'negative'
    };
  };

  // Stats cards with real data
  const stats = filteredData ? [
    {
      title: 'إجمالي العروض',
      value: filteredData.overview.totalOffers.toLocaleString(),
      change: `${filteredData.overview.growthRates.offers > 0 ? '+' : ''}${filteredData.overview.growthRates.offers.toFixed(1)}%`,
      changeType: filteredData.overview.growthRates.offers >= 0 ? 'positive' : 'negative',
      icon: ShoppingBag,
      color: 'primary',
      description: 'جميع العروض المسجلة',
      sparklineData: filteredData.timeSeries?.map(t => t.offers) || []
    },
    {
      title: 'المنتجات المستخرجة',
      value: filteredData.overview.totalProducts.toLocaleString(),
      change: `${filteredData.overview.growthRates.products > 0 ? '+' : ''}${filteredData.overview.growthRates.products.toFixed(1)}%`,
      changeType: filteredData.overview.growthRates.products >= 0 ? 'positive' : 'negative',
      icon: Package,
      color: 'secondary',
      description: 'المنتجات المكتشفة بالذكاء الاصطناعي',
      sparklineData: filteredData.timeSeries?.map(t => t.products) || []
    },
    {
      title: 'متوسط الدقة',
      value: `${Math.round(filteredData.overview.avgConfidence)}%`,
      change: '+2.3%',
      changeType: 'positive',
      icon: Target,
      color: 'success',
      description: 'دقة الذكاء الاصطناعي',
      sparklineData: filteredData.timeSeries?.map(t => t.confidence) || []
    },
    {
      title: 'متوسط الخصم',
      value: `${Math.round(filteredData.overview.avgDiscount)}%`,
      change: `${filteredData.overview.avgDiscount > 15 ? '+' : '-'}${Math.abs(filteredData.overview.avgDiscount - 15).toFixed(1)}%`,
      changeType: filteredData.overview.avgDiscount > 15 ? 'positive' : 'negative',
      icon: Percent,
      color: 'warning',
      description: 'معدل الخصومات',
      sparklineData: []
    },
    {
      title: 'التجار النشطون',
      value: filteredData.overview.totalMerchants.toLocaleString(),
      change: `${filteredData.overview.growthRates.merchants > 0 ? '+' : ''}${filteredData.overview.growthRates.merchants.toFixed(1)}%`,
      changeType: filteredData.overview.growthRates.merchants >= 0 ? 'positive' : 'negative',
      icon: Store,
      color: 'purple',
      description: 'التجار المسجلون',
      sparklineData: []
    },
    {
      title: 'معدل المشاهدات',
      value: filteredData.timeSeries?.reduce((sum, t) => sum + t.views, 0).toLocaleString() || '0',
      change: '+45.2%',
      changeType: 'positive',
      icon: Eye,
      color: 'indigo',
      description: 'إجمالي المشاهدات',
      sparklineData: filteredData.timeSeries?.map(t => t.views) || []
    }
  ] : [];

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              <span className="font-medium">{entry.name}:</span> {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Sparkline component
  const Sparkline = ({ data, color }: { data: number[], color: string }) => {
    if (!data || data.length === 0) return null;
    
    return (
      <ResponsiveContainer width={80} height={30}>
        <LineChart data={data.map((value, index) => ({ value, index }))}>
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="loading-spinner w-16 h-16 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">جاري تحميل الإحصائيات المتقدمة...</p>
          <p className="text-sm text-gray-500 mt-2">
            {isUsingGraphQL ? 'استخدام GraphQL للحصول على البيانات' : 'استخدام REST API'}
          </p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <AlertCircle className="w-16 h-16 text-error-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">خطأ في تحميل البيانات</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => refetch()} className="btn-primary">
              <RefreshCw className="w-4 h-4 ml-2" />
              إعادة المحاولة
            </button>
            <button 
              onClick={() => setUseGraphQL(!useGraphQL)} 
              className="btn-outline"
            >
              تبديل إلى {useGraphQL ? 'REST API' : 'GraphQL'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header with Filters */}
      <div>
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
          <div>
            <h1 className="text-responsive-xl font-bold gradient-text">
              الإحصائيات والتحليلات المتقدمة
            </h1>
            <p className="text-gray-600 mt-2">
              تحليل شامل لأداء النظام مع رؤى تفصيلية
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="btn-outline"
            >
              <Filter className="w-4 h-4 ml-2" />
              فلاتر متقدمة
              <ChevronDown className={`w-4 h-4 mr-2 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
            </button>
            <div className="flex items-center gap-2">
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as any)}
                className="input py-2"
              >
                <option value="csv">CSV</option>
                <option value="json">JSON</option>
                <option value="pdf">PDF</option>
              </select>
              <button onClick={handleExport} className="btn-secondary">
                <FileDown className="w-4 h-4 ml-2" />
                تصدير
              </button>
            </div>
            <button onClick={() => refetch()} className="btn-primary">
              <RefreshCw className="w-4 h-4 ml-2" />
              تحديث
            </button>
          </div>
        </div>

        {/* Advanced Filters */}
        <AnimatePresence>
          {showAdvancedFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-gray-50/50 backdrop-blur-sm rounded-xl p-6 mb-6 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الفترة الزمنية
                  </label>
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="input"
                  >
                    <option value="today">اليوم</option>
                    <option value="last_7_days">آخر 7 أيام</option>
                    <option value="last_30_days">آخر 30 يوم</option>
                    <option value="last_90_days">آخر 90 يوم</option>
                    <option value="last_year">آخر سنة</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    المتجر
                  </label>
                  <select
                    value={selectedMerchant}
                    onChange={(e) => setSelectedMerchant(e.target.value)}
                    className="input"
                  >
                    <option value="all">جميع المتاجر</option>
                    {merchants.map(merchant => (
                      <option key={merchant.id} value={merchant.name}>
                        {merchant.name_ar || merchant.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الفئة
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="input"
                  >
                    <option value="all">جميع الفئات</option>
                    {filteredData?.categories.map(cat => (
                      <option key={cat.name} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    المدينة
                  </label>
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="input"
                  >
                    <option value="all">جميع المدن</option>
                    {cities.map(city => (
                      <option key={city.id} value={city.name_ar}>
                        {city.name_ar}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={useGraphQL}
                      onChange={(e) => setUseGraphQL(e.target.checked)}
                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">استخدام GraphQL</span>
                  </label>
                </div>
                <button
                  onClick={() => {
                    setSelectedMerchant('all');
                    setSelectedCategory('all');
                    setSelectedCity('all');
                    setDateRange('last_30_days');
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  إعادة تعيين الفلاتر
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 space-x-reverse">
          {[
            { id: 'overview', label: 'نظرة عامة', icon: BarChart3 },
            { id: 'performance', label: 'الأداء', icon: TrendingUp },
            { id: 'geographic', label: 'التوزيع الجغرافي', icon: Map },
            { id: 'ai', label: 'أداء الذكاء الاصطناعي', icon: Sparkles }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2
                ${activeTab === tab.id 
                  ? 'border-primary-500 text-primary-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Stats Overview - Always visible */}
      <div className="metrics-grid">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className={`icon-${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
                <div className="flex items-center gap-2">
                  {stat.sparklineData.length > 0 && (
                    <Sparkline 
                      data={stat.sparklineData} 
                      color={stat.changeType === 'positive' ? '#10b981' : '#ef4444'} 
                    />
                  )}
                  <div className={`text-sm font-medium flex items-center gap-1 ${
                    stat.changeType === 'positive' ? 'text-success-600' : 'text-error-600'
                  }`}>
                    {stat.changeType === 'positive' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    {stat.change}
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stat.value}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">{stat.title}</p>
                <p className="text-xs text-gray-600">{stat.description}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            {/* Time Series Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="chart-container"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary-600" />
                    اتجاه النمو الزمني
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={filteredData?.timeSeries || []}>
                    <defs>
                      <linearGradient id="colorOffers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorProducts" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="offers"
                      stroke="#3b82f6"
                      fillOpacity={1}
                      fill="url(#colorOffers)"
                      name="العروض"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="products"
                      stroke="#10b981"
                      fillOpacity={1}
                      fill="url(#colorProducts)"
                      name="المنتجات"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </motion.div>

              {/* Categories Distribution */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="chart-container"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <Package className="w-5 h-5 text-secondary-600" />
                    توزيع الفئات
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={filteredData?.categories.slice(0, 6) || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {filteredData?.categories.slice(0, 6).map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </motion.div>
            </div>

            {/* Merchants Performance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="chart-container"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <Store className="w-5 h-5 text-purple-600" />
                    أداء المتاجر
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={filteredData?.merchants.slice(0, 5) || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="offers" fill="#8b5cf6" name="العروض" />
                    <Bar yAxisId="left" dataKey="products" fill="#06b6d4" name="المنتجات" />
                    <Line 
                      yAxisId="right" 
                      type="monotone" 
                      dataKey="avgDiscount" 
                      stroke="#f59e0b" 
                      strokeWidth={2}
                      name="متوسط الخصم %" 
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </motion.div>

              {/* Price Distribution */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="chart-container"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-success-600" />
                    توزيع الأسعار
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={filteredData?.prices || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="count" fill="#10b981" name="عدد المنتجات" />
                    <Bar dataKey="avgDiscount" fill="#f59e0b" name="متوسط الخصم %" />
                  </BarChart>
                </ResponsiveContainer>
              </motion.div>
            </div>

            {/* Categories Performance Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-gradient"
            >
              <div className="card-header">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-accent-600" />
                  تحليل أداء الفئات
                </h3>
              </div>
              <div className="card-body">
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="table-header">الفئة</th>
                        <th className="table-header">عدد المنتجات</th>
                        <th className="table-header">متوسط الخصم</th>
                        <th className="table-header">النسبة</th>
                        <th className="table-header">الأداء</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200/50">
                      {filteredData?.categories.slice(0, 8).map((category: any, index: number) => (
                        <tr key={category.name} className="hover:bg-gray-50/50 transition-colors">
                          <td className="table-cell">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: category.color }}
                              />
                              <span className="font-medium">{category.name}</span>
                            </div>
                          </td>
                          <td className="table-cell">
                            <span className="font-medium">{category.value}</span>
                          </td>
                          <td className="table-cell">
                            <span className="text-success-600 font-medium">
                              {Math.round(category.avgDiscount)}%
                            </span>
                          </td>
                          <td className="table-cell">
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="h-2 rounded-full transition-all duration-500"
                                  style={{ 
                                    width: `${category.percentage}%`,
                                    backgroundColor: category.color
                                  }}
                                />
                              </div>
                              <span className="text-sm text-gray-600">
                                {category.percentage.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="flex items-center gap-2">
                              {category.avgDiscount > 20 ? (
                                <span className="badge-success">ممتاز</span>
                              ) : category.avgDiscount > 10 ? (
                                <span className="badge-warning">جيد</span>
                              ) : (
                                <span className="badge-error">يحتاج تحسين</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {activeTab === 'performance' && (
          <motion.div
            key="performance"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            {/* Conversion Funnel */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="chart-container"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary-600" />
                  قمع التحويل
                </h3>
              </div>
              <div className="space-y-4">
                {[
                  { label: 'مشاهدات العروض', value: 10000, percentage: 100, color: 'bg-blue-500' },
                  { label: 'تفاعل مع المنتجات', value: 6500, percentage: 65, color: 'bg-indigo-500' },
                  { label: 'مقارنة الأسعار', value: 4200, percentage: 42, color: 'bg-purple-500' },
                  { label: 'زيارة المتجر', value: 2100, percentage: 21, color: 'bg-pink-500' }
                ].map((step, index) => (
                  <div key={step.label} className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{step.label}</span>
                      <span className="text-sm font-medium text-gray-900">{step.value.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-8">
                      <div 
                        className={`${step.color} h-8 rounded-full flex items-center justify-end px-3 transition-all duration-700`}
                        style={{ width: `${step.percentage}%` }}
                      >
                        <span className="text-white text-sm font-medium">{step.percentage}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Merchant Performance Radar */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="chart-container"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <Target className="w-5 h-5 text-secondary-600" />
                    مقارنة أداء المتاجر
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={[
                    { metric: 'العروض', A: 80, B: 65, C: 90 },
                    { metric: 'المنتجات', A: 75, B: 85, C: 70 },
                    { metric: 'الخصومات', A: 90, B: 70, C: 85 },
                    { metric: 'الدقة', A: 85, B: 80, C: 75 },
                    { metric: 'التفاعل', A: 70, B: 90, C: 80 }
                  ]}>
                    <PolarGrid strokeDasharray="3 3" />
                    <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar name="كارفور" dataKey="A" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                    <Radar name="بنده" dataKey="B" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                    <Radar name="العثيم" dataKey="C" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} />
                    <Legend />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </motion.div>

              {/* Performance Metrics */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
                  <Activity className="w-5 h-5 text-purple-600" />
                  مؤشرات الأداء الرئيسية
                </h3>
                {[
                  { 
                    kpi: 'معدل التحويل', 
                    value: '21%', 
                    target: '25%', 
                    progress: 84,
                    trend: 'up',
                    color: 'primary' 
                  },
                  { 
                    kpi: 'متوسط قيمة العرض', 
                    value: '156 ريال', 
                    target: '200 ريال', 
                    progress: 78,
                    trend: 'up',
                    color: 'secondary' 
                  },
                  { 
                    kpi: 'معدل الاحتفاظ', 
                    value: '68%', 
                    target: '75%', 
                    progress: 91,
                    trend: 'down',
                    color: 'warning' 
                  },
                  { 
                    kpi: 'دقة الاستخراج', 
                    value: '92%', 
                    target: '95%', 
                    progress: 97,
                    trend: 'up',
                    color: 'success' 
                  }
                ].map((kpi) => (
                  <div key={kpi.kpi} className="bg-white/50 backdrop-blur-sm rounded-lg p-4 border border-gray-200/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{kpi.kpi}</span>
                      <div className="flex items-center gap-2">
                        {kpi.trend === 'up' ? (
                          <ArrowUp className="w-4 h-4 text-success-600" />
                        ) : (
                          <ArrowDown className="w-4 h-4 text-error-600" />
                        )}
                        <span className="text-lg font-bold text-gray-900">{kpi.value}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                      <span>الحالي</span>
                      <span>الهدف: {kpi.target}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`bg-${kpi.color}-500 h-2 rounded-full transition-all duration-500`}
                        style={{ width: `${kpi.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}

        {activeTab === 'geographic' && (
          <motion.div
            key="geographic"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Geographic Distribution */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="chart-container"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <Map className="w-5 h-5 text-primary-600" />
                    التوزيع الجغرافي للعروض
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <Treemap
                    data={[
                      { name: 'الرياض', size: 450, fill: '#3b82f6' },
                      { name: 'جدة', size: 380, fill: '#10b981' },
                      { name: 'الدمام', size: 220, fill: '#f59e0b' },
                      { name: 'مكة', size: 180, fill: '#8b5cf6' },
                      { name: 'المدينة', size: 150, fill: '#ef4444' },
                      { name: 'الخبر', size: 120, fill: '#06b6d4' }
                    ]}
                    dataKey="size"
                    ratio={4/3}
                    stroke="#fff"
                    fill="#8884d8"
                  >
                    <Tooltip content={<CustomTooltip />} />
                  </Treemap>
                </ResponsiveContainer>
              </motion.div>

              {/* City Performance Table */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="card-gradient h-fit"
              >
                <div className="card-header">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <Store className="w-5 h-5 text-secondary-600" />
                    أداء المدن
                  </h3>
                </div>
                <div className="card-body">
                  <div className="space-y-3">
                    {filteredData?.geographic?.slice(0, 6).map((city: any, index: number) => (
                      <div key={city.city} className="flex items-center justify-between p-3 bg-gray-50/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center font-bold text-primary-600">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{city.city}</p>
                            <p className="text-sm text-gray-600">التاجر الأول: {city.topMerchant}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg text-gray-900">{city.offers}</p>
                          <p className="text-sm text-gray-600">{city.products} منتج</p>
                        </div>
                      </div>
                    )) || (
                      <div className="text-center text-gray-500 py-8">
                        <Map className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>لا توجد بيانات جغرافية متاحة</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {activeTab === 'ai' && (
          <motion.div
            key="ai"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            {/* AI Performance Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Confidence Distribution */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="chart-container"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <Target className="w-5 h-5 text-indigo-600" />
                    توزيع مستوى الثقة
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={filteredData?.confidence || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="عدد المنتجات">
                      {filteredData?.confidence.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </motion.div>

              {/* Processing Time Analysis */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="chart-container"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-600" />
                    أوقات المعالجة
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[
                    { time: '00:00', yolo: 0.8, gemini: 1.2, total: 2.0 },
                    { time: '04:00', yolo: 0.7, gemini: 1.1, total: 1.8 },
                    { time: '08:00', yolo: 0.9, gemini: 1.3, total: 2.2 },
                    { time: '12:00', yolo: 1.1, gemini: 1.5, total: 2.6 },
                    { time: '16:00', yolo: 1.0, gemini: 1.4, total: 2.4 },
                    { time: '20:00', yolo: 0.9, gemini: 1.3, total: 2.2 }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} label={{ value: 'الوقت (ثانية)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="yolo" stroke="#3b82f6" strokeWidth={2} name="YOLO Detection" />
                    <Line type="monotone" dataKey="gemini" stroke="#10b981" strokeWidth={2} name="Gemini Analysis" />
                    <Line type="monotone" dataKey="total" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" name="الإجمالي" />
                  </LineChart>
                </ResponsiveContainer>
              </motion.div>
            </div>

            {/* AI Model Performance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-gradient"
            >
              <div className="card-header">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-accent-600" />
                  أداء نماذج الذكاء الاصطناعي
                </h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    {
                      model: 'YOLO v8',
                      metrics: {
                        accuracy: 94.5,
                        precision: 92.3,
                        recall: 96.1,
                        f1Score: 94.2
                      },
                      avgTime: '0.85s',
                      totalProcessed: 15420
                    },
                    {
                      model: 'Gemini Pro',
                      metrics: {
                        accuracy: 91.2,
                        precision: 89.5,
                        recall: 93.0,
                        f1Score: 91.2
                      },
                      avgTime: '1.32s',
                      totalProcessed: 15420
                    },
                    {
                      model: 'Combined Pipeline',
                      metrics: {
                        accuracy: 92.8,
                        precision: 90.9,
                        recall: 94.5,
                        f1Score: 92.7
                      },
                      avgTime: '2.17s',
                      totalProcessed: 15420
                    }
                  ].map((model) => (
                    <div key={model.model} className="bg-white/50 backdrop-blur-sm rounded-lg p-6 border border-gray-200/50">
                      <h4 className="font-bold text-gray-900 mb-4">{model.model}</h4>
                      <div className="space-y-3">
                        {Object.entries(model.metrics).map(([key, value]) => (
                          <div key={key}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                              <span className="font-medium text-gray-900">{value}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className="bg-gradient-to-r from-primary-500 to-secondary-500 h-1.5 rounded-full transition-all duration-500"
                                style={{ width: `${value}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">متوسط الوقت</span>
                          <span className="font-medium text-gray-900">{model.avgTime}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-2">
                          <span className="text-gray-600">معالج</span>
                          <span className="font-medium text-gray-900">{model.totalProcessed.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Analytics;